import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
  OnDestroy,
  QueryList,
  signal,
  ViewChildren,
} from "@angular/core";
import { Subscription } from "rxjs";
import { ViewerConstants } from "../../../../shared/model/viewer-constants";
import { MeasurementTool } from "../../../model/measurementtool";
import { DxmDownloadStatus } from "../../../model/dxm-download-status";
import {
  GoogleAnalyticsConstantsCategory,
  GoogleAnalyticsConstantsEvents,
} from "../../../../shared/model/google-analytics-constants";
import { PhoenixExportFeaturesComponent } from "../../../../dialog-components/export-layer/phoenix-export-features.component";
import { MapService } from "../../../services/internal-services/map.service";
import { IMeasurementFeature } from "../../../interfaces/i-measurement-feature";
import { IMetadataImage } from "../../../interfaces/i-metadataimage";
import { ImagesService } from "../../../services/internal-services/images.service";
import { MatDialog } from "@angular/material/dialog";
import { GoogleAnalyticsService } from "../../../../shared/services/google-analytics.service";
import { VIEWER_SERVICE } from "../../../services/internal-services/viewer-services/inject-base-viewer.service";
import { CdkConnectedOverlay } from "@angular/cdk/overlay";
import { OverlayRepositionService } from "../../../services/internal-services/overlay-reposition.service";

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: "app-measurement-actions",
  templateUrl: "./measurement-actions.component.html",
  styleUrls: ["./measurement-actions.component.scss"],
  standalone: false,
})
export class MeasurementActionsComponent implements AfterViewInit, OnDestroy {
  viewerService = inject(VIEWER_SERVICE);

  private overlayRepositionService = inject(OverlayRepositionService);

  @ViewChildren(CdkConnectedOverlay) private connectedOverlays!: QueryList<CdkConnectedOverlay>;

  tools = ViewerConstants.EXTERNAL_TOOLS;

  selectedTool = null;

  selectedFeature = null;

  holdTool = null; // save selected tool while loading tiles

  isExportLayerDialogOpen = false;

  measurementLayer: any;

  showMeasurements = true;

  _mode3D = false;

  enabled3D = false; // property that determinate if we are in a layer with dsm and also have info (coverage)

  insideDsm = false;

  _dsmStatus = DxmDownloadStatus.stop;

  selectActionEnabled = false;

  editColorActionEnabled = false;

  editActionEnabled = false;

  dragActionEnabled = false;

  /** Default measurement color — must include alpha so the forced-alpha picker resets fully. */
  protected readonly defaultColor = "rgba(255,196,0,0.2)";

  protected readonly currentColor = signal(this.defaultColor);

  private measuresSelected: any[] = [];

  private measuresSelectedSub?: Subscription;

  _oblique = false;

  private _mode: any;

  @Input() set mode(mode: any) {
    this._mode = mode;
    this.mode3D = mode ? mode.name === ViewerConstants.COLLECTION_MODES.OBLIQUE.name : false;
  }

  get mode(): any {
    return this._mode;
  }

  @Input()
  set discreteEnabled(enabled: boolean) {
    // this.oblique = enabled;
  }

  @Input()
  debugMode: boolean = false;

  @Input()
  set dsmEvent(event: any) {
    this.enabled3D = event.enabled;
    this.insideDsm = event.inside;
    this.dsmStatus = event.status;

    // When DSM is enabled, we are inside coverage, and DSM loading is finished,
    // ensure measurement features and their tooltips are visible again.
    // This fixes the case where visiting a 3D collection with legacy data could hide them.
    if (this.enabled3D && this.insideDsm && this.dsmStatus === DxmDownloadStatus.finished) {
      this.refreshMeasurementTooltips();
    }
  }

  @Input()
  set image(image: IMetadataImage) {
    if (image) {
      this.deactivateSelection();
      this.oblique = ImagesService.isOblique(image.orientation);
    }
  }

  @Input()
  set measures(features: IMeasurementFeature[]) {
    if (features?.length) {
      this.selectedFeature = features[0];
    } else {
      this.selectedFeature = null;
    }
  }

  @Input()
  measurementTools: MeasurementTool[];

  @Input()
  set deactivate(deactivate: boolean) {
    this.cancelTools(deactivate);
  }

  @Input()
  set exportFeature(features: any[]) {
    if (features) {
      this.openExportDialog("Export Feature", features);

      this.googleAnalyticsService.setGoogleAnalyticsEvent(
        GoogleAnalyticsConstantsEvents.EXPORT_GEOMETRY_RIGHT_CLICK,
        GoogleAnalyticsConstantsCategory.CONTEXT_MENU,
        true,
      );
    }
  }

  constructor(
    protected googleAnalyticsService: GoogleAnalyticsService,
    protected mapService: MapService,
    protected matDialog: MatDialog,
  ) {
    this.measurementTools = this.viewerService.getMeasurementTools();
    this.measurementLayer = this.mapService.getMeasurementLayer();
  }

  ngAfterViewInit() {
    this.connectedOverlays.forEach((overlay) => this.overlayRepositionService.register(overlay));
    this.measuresSelectedSub = this.viewerService.measuresSelected$.subscribe(
      (m) => { this.measuresSelected = m ?? []; },
    );
  }

  ngOnDestroy() {
    this.measuresSelectedSub?.unsubscribe();
    this.connectedOverlays?.forEach((overlay) => this.overlayRepositionService.unregister(overlay));
    this.deactivateTools();
    this.deactivateEdit();
    this.deactivateDrag();
    this.deactivateSelection();
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  selectTool(tool: any) {
    if (tool !== null && tool !== this.selectedTool) {
      this.cancelTools(true);
      this.selectedTool = tool;
      this.activateTool(tool);
    } else {
      this.cancelTools(true);
      this.deactivateTools();
    }
  }

  deactivateTools() {
    this.measurementTools
      .filter((tool) => tool.active)
      .map((tool) => {
        tool.deactivate();
        return tool;
      });
    this.selectedTool = null;
  }

  cancelTools(deactivate: boolean) {
    if (deactivate) {
      this.measurementTools.map((tool) => {
        if (tool.active) {
          tool.cancel();
        }
        return tool;
      });
    }
  }

  activateTool(toolId: number) {
    this.measurementTools.map((tool) => {
      if (toolId === tool.id) {
        tool.activate();
      } else {
        tool.deactivate();
      }
      return tool;
    });
    this.deactivateSelection();
    this.deactivateEdit();
    this.deactivateDrag();
  }

  existFeatures() {
    return this.measurementLayer && this.measurementLayer.getSource()
      ? this.measurementLayer.getSource().getFeatures().length > 0
      : false;
  }

  isFeatureSelected(): boolean {
    return !!this.selectedFeature;
  }

  toggleMeasurements() {
    this.showMeasurements = !this.showMeasurements;
    this.measurementTools.map((tool) => {
      tool.tooltipVisibility(this.showMeasurements);
      return tool;
    });
  }

  /*
    Toggles color fill component
    */
  selectColorFill() {
    this.selectActionEnabled = false;
    if (this.editColorActionEnabled) {
      this.deactivateColorPicker();
    } else {
      this.activateColorPicker();
    }

    this.googleAnalyticsService.setGoogleAnalyticsEvent(
      GoogleAnalyticsConstantsEvents.MEASUREMENT_EDIT_COLOR,
      GoogleAnalyticsConstantsCategory.MEASUREMENT_TOOLBAR,
      !this.editColorActionEnabled,
    );
  }

  undoShape() {
    setTimeout(() => {
      this.deactivateSelection();
      this.deactivateEdit();
      this.deactivateDrag();
      this.removeLastPointInfoPopup();
      this.mapService.getCoreMap().removeLastMeasurementFeature();
      const features = this.mapService.getMeasurementLayer().getSource().getFeatures();
      if (features.length === 0) {
        this.deactivateColorPicker();
      }
    }, 150);

    this.googleAnalyticsService.setGoogleAnalyticsEvent(
      GoogleAnalyticsConstantsEvents.MEASUREMENT_UNDO_SHAPE,
      GoogleAnalyticsConstantsCategory.MEASUREMENT_TOOLBAR,
      true,
    );
  }

  deleteSelectedFeature() {
    if (this.selectedFeature) {
      this.mapService.getCoreMap().removeMeasurementFeature(this.selectedFeature.properties.order);
      this.deactivateSelection();
      this.deactivateEdit();
      this.deactivateDrag();
      this.deactivateColorPicker();
      this.selectedFeature = null;
    }
  }

  removeLastPointInfoPopup() {
    this.viewerService.removePointInfoPopupForLastMeasurementFeature();
  }

  clearWorkspace() {
    this.deactivateSelection();
    this.deactivateEdit();
    this.deactivateDrag();
    this.deactivateColorPicker();
    this.mapService.getCoreMap().clearWorkspace();
    this.viewerService.closePointInfoPopups();

    this.googleAnalyticsService.setGoogleAnalyticsEvent(
      GoogleAnalyticsConstantsEvents.MEASUREMENT_CLEAR_WORKSPACE,
      GoogleAnalyticsConstantsCategory.MEASUREMENT_TOOLBAR,
      true,
    );
  }

  showExportMeasurementLayerDialog() {
    this.deactivateSelection();
    this.deactivateEdit();
    this.deactivateDrag();
    this.deactivateColorPicker();

    this.openExportDialog("Export Layer", this.measurementLayer.getSource().getFeatures());

    this.googleAnalyticsService.setGoogleAnalyticsEvent(
      GoogleAnalyticsConstantsEvents.EXPORT_GEOMETRY_CLICK_TOOL,
      GoogleAnalyticsConstantsCategory.MEASUREMENT_TOOLBAR,
      true,
    );
  }

  openExportDialog(title: string, features: any[]) {
    this.isExportLayerDialogOpen = true;
    const dialog = this.matDialog.open(PhoenixExportFeaturesComponent, {
      data: {
        title: title,
        viewerService: this.viewerService,
        googleAnalyticsService: this.googleAnalyticsService,
        features: features,
      },
    });
    dialog.afterClosed().subscribe(() => {
      this.isExportLayerDialogOpen = false;
    });
  }

  getInfoTooltip(): string {
    if (!this.existFeatures()) {
      return "Draw & select shape to get info";
    }
    if (this.selectActionEnabled) {
      return "Select a line or shape to get info";
    }
    return "Selection Tool";
  }

  toggleDrag() {
    this.deactivateTools();
    if (this.dragActionEnabled) {
      this.deactivateDrag();
    } else {
      this.activateDrag();
    }

    this.googleAnalyticsService.setGoogleAnalyticsEvent(
      GoogleAnalyticsConstantsEvents.MEASUREMENT_EDIT_FEATURE,
      GoogleAnalyticsConstantsCategory.MEASUREMENT_TOOLBAR,
      !this.dragActionEnabled,
    );
  }

  toggleEdit() {
    this.deactivateTools();
    if (this.editActionEnabled) {
      this.deactivateEdit();
    } else {
      this.activateEdit();
    }

    this.googleAnalyticsService.setGoogleAnalyticsEvent(
      GoogleAnalyticsConstantsEvents.MEASUREMENT_DRAG_FEATURE,
      GoogleAnalyticsConstantsCategory.MEASUREMENT_TOOLBAR,
      !this.editActionEnabled,
    );
  }

  deactivateDrag() {
    this.dragActionEnabled = false;
    this.viewerService.disableDragMeasurement();
  }

  activateDrag() {
    this.dragActionEnabled = true;
    this.deactivateSelection();
    this.deactivateEdit();
    this.viewerService.enableDragMeasurement();
  }

  deactivateEdit() {
    this.editActionEnabled = false;
    this.viewerService.disableEditMeasurement();
  }

  activateEdit() {
    this.editActionEnabled = true;
    this.deactivateSelection();
    this.deactivateDrag();
    this.viewerService.enableEditMeasurement();
  }

  toggleSelection() {
    this.deactivateTools();
    if (this.selectActionEnabled) {
      this.deactivateSelection();
    } else {
      this.activateSelection();
    }
  }

  deactivateSelection() {
    this.selectActionEnabled = false;
    this.viewerService.disableMeasuresSelection();
  }

  activateSelection() {
    this.selectActionEnabled = true;
    this.deactivateColorPicker();
    this.viewerService.enableMeasuresSelection();
    this.deactivateEdit();
    this.deactivateDrag();
  }

  deactivateColorPicker() {
    this.editColorActionEnabled = false;
  }

  activateColorPicker() {
    this.deactivateTools();
    this.viewerService.enableMeasuresSelection();
    this.editColorActionEnabled = true;
    this.currentColor.set(this.viewerService.getColorRgbA().toString());
    this.deactivateEdit();
    this.deactivateDrag();
  }

  protected applyColor(color: string): void {
    if (color.includes("#")) {
      this.viewerService.setColor(color, this.measuresSelected);
    } else {
      this.viewerService.setColorRgbA(color, this.measuresSelected);
    }
  }

  protected cancelColor(): void {
    this.currentColor.set(this.defaultColor);
  }

  set mode3D(enable) {
    this._mode3D = enable;
    if (!enable) {
      this.deactivateTools();
      // When switching to 2D, restore the measurement layer and tooltips.
      // A prior visit to a 3D Gray Sky collection may have called hideMeasurementLayer()
      // which removes tooltip overlays from the map — refresh them so they exist again.
      this.refreshMeasurementTooltips();
    }
  }

  refreshMeasurementTooltips() {
    try {
      if (this.measurementLayer && typeof this.measurementLayer.setVisible === "function") {
        this.measurementLayer.setVisible(true);
      }
    } catch (e) {
      // no-op: some layer implementations may not have setVisible
    }

    this.showMeasurements = true;
    this.measurementTools?.forEach((tool) => {
      if (tool?.internalTool && typeof tool.internalTool.refreshToolTips === "function") {
        tool.internalTool.refreshToolTips();
      }
    });
  }

  get mode3D(): boolean {
    return this._mode3D;
  }

  getTooltipMessage(tool: number): string {
    if (this.isSelected(tool)) {
      return null;
    }

    if (this.mode3D && this.dsmStatus === DxmDownloadStatus.downloading) {
      return "3D measuring tools not yet available.";
    }

    if (
      this.mode3D &&
      this.dsmStatus === DxmDownloadStatus.stop &&
      !this.enabled3D &&
      tool !== this.tools.MEASURE_HEIGHT
    ) {
      return "3D measuring tools are not compatible with this collection.";
    }

    switch (tool) {
      case this.tools.MEASURE_POINT:
      case this.tools.MEASURE_POINT_3D:
      case this.tools.MEASURE_ELEVATION:
        return "Mark point and/or get elevation";
      case this.tools.MEASURE_POINT_SURVEY:
        return "Mark point and get elevation over survey";
      case this.tools.MEASURE_LINE:
        return "Draw line";
      case this.tools.MEASURE_POLYGON:
        return "Draw polygon";
      case this.tools.MEASURE_CIRCLE:
        return "Draw circle";
      case this.tools.MEASURE_AREA:
        return "Draw square";
      case this.tools.RELATIVE_MEASURE_POINT:
        if (this.mode3D && !this.enabled3D) {
          return "Relative Height tool is not compatible with this collection";
        }
        return "Measure relative height";
      case this.tools.MEASURE_HEIGHT:
        if (!this.mode3D) {
          return "Height tool is not compatible with 2D imagery";
        }

        if (!this.oblique) {
          return "Height tool is not compatible with vertical imagery";
        }

        return "Draw height";
      default:
        return null;
    }
  }

  isSelected(tool: number) {
    return this.selectedTool === tool;
  }

  /**
   * Return if the 3D tools should be enabled (height tool)
   * When we are in 3d mode and
   * If we are in a place with dsm enable we have to finish downloading tiles
   * Force status to stop if we are in a place without dsm
   */
  get status3D(): string {
    return !this.enabled3D ? DxmDownloadStatus.stop : this.dsmStatus;
  }

  /**
   * Set if the view its oblique or not, oblique are North, south, east and west orientation
   * If we change orientation, we must deactivate tools and also deactivate the selected feature
   * @param oblique
   */
  set oblique(oblique: boolean) {
    this._oblique = oblique;
    this.showMeasurements = !oblique;
    this.deactivateTools();
    this.deactivateSelection();
  }

  get oblique(): boolean {
    return this._oblique;
  }

  /**
   * Update dsm status,
   * if we are downloading tiles we disable the tools until we finish with the download
   * @param status
   */
  set dsmStatus(status: DxmDownloadStatus) {
    if (status === this.dsmStatus) {
      return;
    }
    if (status === DxmDownloadStatus.downloading && this.selectedTool !== null) {
      this.holdTool = this.selectedTool;
    }
    this._dsmStatus = status;
  }

  get dsmStatus(): DxmDownloadStatus {
    return this._dsmStatus;
  }

  get toolsDisabled(): boolean {
    return (this.mode3D && !this.enabled3D) || this.dsmStatus === DxmDownloadStatus.downloading;
  }

  get heightToolDisabled(): boolean {
    return this.toolsDisabled || !this.oblique || !this._mode3D;
  }

  get relativeHeightToolDisabled(): boolean {
    return this.toolsDisabled || !this.oblique || !this.enabled3D;
  }
}
